import { diffUtils } from '@gitbook/doc-core';
import type { Fragment } from '@gitbook/slate';

import type { FormattingOptions } from './options';

export interface FormattingContext extends FormattingOptions {
    /**
     * Allocate a new numbered ID for a footnote.
     */
    allocateFootnote: (fragment: Fragment) => string;

    /**
     * List all footnotes with their associated IDs.
     */
    getFootnotes: () => Array<[string, Fragment]>;
}

/**
 * Create the local formatting context.
 */
export function createFormattingContext(
    options: FormattingOptions | FormattingContext
): FormattingContext {
    if (isFormattingContext(options)) {
        return options;
    }

    const annotations: Fragment[] = [];

    return {
        ...options,
        allocateFootnote: (fragment: Fragment) => {
            // Check if the fragment already exists in the list of annotations and return its index
            const existing = annotations.findIndex(
                (existingFragment) =>
                    // 0 means the fragments are equal
                    diffUtils.compareSlateNodesIgnoringKeys(existingFragment, fragment) === 0
            );
            if (existing !== -1) {
                return `${existing + 1}`;
            }

            annotations.push(fragment);
            return `${annotations.length}`;
        },
        getFootnotes: () => annotations.map((fragment, index) => [`${index + 1}`, fragment]),
    };
}

function isFormattingContext(
    options: FormattingOptions | FormattingContext
): options is FormattingContext {
    return 'allocateFootnote' in options;
}
